import React, { useState, useEffect, useCallback } from 'react';
import { TextField, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import SSHKeyConfiguration from './SSHKeyConfiguration';
import { useTenants } from '../hooks/useTenants';
import { useCreateSSHConnection, useUpdateGitHubConnection } from '../hooks/useGitHubConnection';

interface GithubSshConfig {
    tenantId: string;
    name: string;
    description?: string;
    repositoryUrl: string;
    accessMethod: 'ssh';
    connectionType?: 'repository' | 'organization' | 'user';
    keyType?: 'rsa' | 'ed25519';
    isActive: boolean;
    secretArn?: string;
    publicKey: string;
    createdAt: string;
    updatedAt: string;
}

interface GitHubInstallation {
    connectionId: string;
    creationDate: string;
    installationId: string;
    accountLogin: string;
    accountType: 'User' | 'Organization';
    repositorySelection: 'all' | 'selected';
    permissions: any;
    isActive: boolean;
    ownerEmail: string;
    name: string;
    description?: string;
    updatedAt: string;
}

interface GitHubConnectionEditProps {
    connection?: GithubSshConfig;
    accessMethod?: 'ssh';
    installations?: GitHubInstallation[];
    onSave?: (connection: Partial<GithubSshConfig>) => void;
    onCancel: () => void;
    editorActions?: any;
    setEditorState?: (state: any) => void;
}

const GitHubConnectionEdit: React.FC<GitHubConnectionEditProps> = ({
    connection,
    accessMethod: initialAccessMethod,
    installations = [],
    onSave,
    onCancel,
    editorActions,
    setEditorState
}) => {
    const [formData, setFormData] = useState({
        tenantId: '',
        name: '',
        description: '',
        repositoryUrl: '',
        accessMethod: 'ssh' as 'ssh',
        connectionType: 'repository' as 'repository' | 'organization' | 'user',
        keyType: 'ed25519' as 'rsa' | 'ed25519',
        isActive: true
    });

    const { data: tenants = [] } = useTenants();
    const createSSHConnection = useCreateSSHConnection();
    const updateConnection = useUpdateGitHubConnection();

    const isFormValid = formData.tenantId.trim().length > 0 &&
                       formData.name.trim().length > 0 &&
                       formData.repositoryUrl.trim().length > 0;

    useEffect(() => {
        if (connection) {
            setFormData({
                tenantId: connection.tenantId,
                name: connection.name,
                description: connection.description || '',
                repositoryUrl: connection.repositoryUrl,
                accessMethod: 'ssh',
                connectionType: connection.connectionType || 'repository',
                keyType: connection.keyType || 'ed25519',
                isActive: connection.isActive
            });
        }
    }, [connection]);

    useEffect(() => {
        if (setEditorState) {
            setEditorState({ saveEnabled: isFormValid });
        }
    }, [setEditorState, isFormValid]);

    const handleSave = useCallback(async () => {
        const { keyType, ...saveData } = formData;
        const connectionData = {
            ...saveData,
            keyType: formData.accessMethod === 'ssh' ? keyType : undefined
        };

        try {
            if (connection) {
                // Update existing connection
                await updateConnection.mutateAsync({
                    ...connectionData,
                    connectionId: `${connection.tenantId}-${connection.repositoryUrl}`
                });
            } else {
                // Create new connection based on access method
                await createSSHConnection.mutateAsync(connectionData);
            }
            onSave?.(connectionData);
            onCancel();
        } catch (error) {
            console.error('Failed to save connection:', error);
        }
    }, [formData, connection, updateConnection, createSSHConnection, onSave, onCancel]);

    useEffect(() => {
        if (editorActions) {
            editorActions.onSave = handleSave;
            editorActions.onCancel = onCancel;
        }
    }, [editorActions, onCancel, handleSave]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
                <InputLabel>Tenant</InputLabel>
                <Select
                    value={formData.tenantId}
                    label="Tenant"
                    onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                >
                    {tenants.map(tenant => (
                        <MenuItem key={tenant.id} value={tenant.id}>
                            {tenant.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                label="Connection Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                placeholder="My GitHub Connection"
                helperText="A friendly name for this connection"
            />

            <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="Description of this connection..."
                helperText="Optional description"
            />

            <FormControl fullWidth required>
                <InputLabel>Connection Type</InputLabel>
                <Select
                    value={formData.connectionType}
                    label="Connection Type"
                    onChange={(e) => setFormData({ ...formData, connectionType: e.target.value as 'repository' | 'organization' | 'user' })}
                >
                    <MenuItem value="repository">Repository</MenuItem>
                    <MenuItem value="organization">Organization</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                </Select>
            </FormControl>

            <TextField
                label={formData.connectionType === 'repository' ? 'Repository URL' : formData.connectionType === 'organization' ? 'Organization URL' : 'User URL'}
                value={formData.repositoryUrl}
                onChange={(e) => setFormData({ ...formData, repositoryUrl: e.target.value })}
                required
                fullWidth
                placeholder={formData.connectionType === 'repository' ? 'https://github.com/owner/repository' : formData.connectionType === 'organization' ? 'https://github.com/organization' : 'https://github.com/username'}
                helperText={`GitHub ${formData.connectionType} URL`}
            />



            <FormControl fullWidth>
                <InputLabel>SSH Key Type</InputLabel>
                <Select
                    value={formData.keyType}
                    label="SSH Key Type"
                    onChange={(e) => setFormData({ ...formData, keyType: e.target.value as 'rsa' | 'ed25519' })}
                    disabled={!!connection?.publicKey}
                >
                    <MenuItem value="ed25519">ED25519 (Recommended)</MenuItem>
                    <MenuItem value="rsa">RSA 2048</MenuItem>
                </Select>
            </FormControl>
            
            <SSHKeyConfiguration
                existingPublicKey={connection?.publicKey}
                keyType={formData.keyType}
                connectionId={connection ? `${connection.tenantId}-${connection.repositoryUrl}` : undefined}
            />






        </Box>
    );
};

export default GitHubConnectionEdit;